/**
 * VijayRasPage — owner-only monthly ledger + settlement calculator for
 * father's store (Vijay Ras Bhandar).
 *
 * Flow:
 *   1. Pick a month.
 *   2. See aggregated IN/OUT totals per item (from all daily sheets in the month).
 *   3. Enter unit prices for each item that had non-zero quantity.
 *   4. Live-compute net settlement:
 *        net_owed = incoming_total - outgoing_total
 *        (positive → we pay them · negative → they pay us)
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Milk, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '../lib/api';

function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function VijayRasPage() {
    const [month, setMonth] = useState(currentMonth());
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null); // {entries, incoming_totals, outgoing_totals}
    const [incomingPrices, setIncomingPrices] = useState({}); // {NAME: price}
    const [outgoingPrices, setOutgoingPrices] = useState({});
    const [settlement, setSettlement] = useState(null);
    const [computing, setComputing] = useState(false);

    const load = async (m) => {
        setLoading(true);
        setSettlement(null);
        try {
            const r = await api.vrMonth(m);
            setData(r);
            // Reset price maps to whatever's in the aggregate; user can edit.
            const inKeys = Object.keys(r?.incoming_totals || {});
            const outKeys = Object.keys(r?.outgoing_totals || {});
            setIncomingPrices((prev) => {
                const next = {};
                inKeys.forEach((k) => (next[k] = prev[k] ?? 0));
                return next;
            });
            setOutgoingPrices((prev) => {
                const next = {};
                outKeys.forEach((k) => (next[k] = prev[k] ?? 0));
                return next;
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load Vijay Ras month');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(month);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month]);

    /** Live-compute totals purely on the client — mirror of the backend
     *  math, so the user gets instant feedback before hitting "Compute". */
    const liveNet = useMemo(() => {
        if (!data) return null;
        let inT = 0;
        let outT = 0;
        Object.entries(data.incoming_totals || {}).forEach(([k, qty]) => {
            inT += (Number(qty) || 0) * (Number(incomingPrices[k]) || 0);
        });
        Object.entries(data.outgoing_totals || {}).forEach(([k, qty]) => {
            outT += (Number(qty) || 0) * (Number(outgoingPrices[k]) || 0);
        });
        return { inT, outT, net: inT - outT };
    }, [data, incomingPrices, outgoingPrices]);

    const compute = async () => {
        setComputing(true);
        try {
            const r = await api.vrSettlement({
                month,
                incoming_prices: incomingPrices,
                outgoing_prices: outgoingPrices,
            });
            setSettlement(r);
            toast.success(
                `Net ${r.net_owed >= 0 ? 'you pay them' : 'they pay you'} ₹${Math.abs(
                    r.net_owed
                ).toLocaleString('en-IN')}`
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Compute failed');
        } finally {
            setComputing(false);
        }
    };

    return (
        <div data-testid="vijay-ras-page" className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
            <div>
                <h1 className="font-heading text-2xl flex items-center gap-2">
                    <Milk size={22} className="text-primary" /> Vijay Ras Bhandar
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Father's store — monthly quantity ledger. Prices are entered at month-end for settlement.
                </p>
            </div>

            <Card className="p-4">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Month</Label>
                <Input
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value || currentMonth())}
                    className="mt-1 max-w-xs"
                    data-testid="vr-month-input"
                />
            </Card>

            {loading ? (
                <Card className="p-10 text-center text-muted-foreground">
                    <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                    Loading…
                </Card>
            ) : (
                <>
                    {/* Two-column ledger */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            {
                                title: 'Incoming (we bought from them)',
                                totals: data?.incoming_totals || {},
                                prices: incomingPrices,
                                setter: setIncomingPrices,
                                prefix: 'vr-in',
                            },
                            {
                                title: 'Outgoing (we sold to them)',
                                totals: data?.outgoing_totals || {},
                                prices: outgoingPrices,
                                setter: setOutgoingPrices,
                                prefix: 'vr-out',
                            },
                        ].map((col) => {
                            const rows = Object.entries(col.totals);
                            const sectionTotal = rows.reduce(
                                (a, [k, qty]) => a + (Number(qty) || 0) * (Number(col.prices[k]) || 0),
                                0
                            );
                            return (
                                <Card key={col.title} className="p-4">
                                    <div className="font-heading text-sm mb-3">{col.title}</div>
                                    {rows.length === 0 ? (
                                        <div className="text-xs text-muted-foreground py-4 text-center">
                                            No entries for this month.
                                        </div>
                                    ) : (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Item</TableHead>
                                                    <TableHead className="text-right w-20">Qty</TableHead>
                                                    <TableHead className="text-right w-24">₹/unit</TableHead>
                                                    <TableHead className="text-right w-24">Line ₹</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {rows.map(([name, qty]) => {
                                                    const price = Number(col.prices[name]) || 0;
                                                    const line = qty * price;
                                                    return (
                                                        <TableRow key={name}>
                                                            <TableCell className="font-medium text-sm">{name}</TableCell>
                                                            <TableCell className="text-right font-mono text-sm">
                                                                {qty}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                <Input
                                                                    type="number"
                                                                    inputMode="decimal"
                                                                    value={col.prices[name] ?? 0}
                                                                    onChange={(e) =>
                                                                        col.setter({
                                                                            ...col.prices,
                                                                            [name]: Number(e.target.value || 0),
                                                                        })
                                                                    }
                                                                    className="h-7 text-xs text-right font-mono w-20 ml-auto"
                                                                    data-testid={`${col.prefix}-price-${name}`}
                                                                />
                                                            </TableCell>
                                                            <TableCell className="text-right font-mono text-sm">
                                                                ₹{line.toLocaleString('en-IN')}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    )}
                                    <div className="text-right text-sm mt-3 pt-3 border-t border-border/60">
                                        Total{' '}
                                        <span className="font-mono font-medium" data-testid={`${col.prefix}-total`}>
                                            ₹{sectionTotal.toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Live net */}
                    {liveNet && (
                        <Card className="p-4">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                                        Net settlement for {month}
                                    </div>
                                    <div
                                        className={`text-2xl font-mono font-medium mt-1 ${
                                            liveNet.net >= 0 ? 'text-destructive' : 'text-green-700'
                                        }`}
                                        data-testid="vr-live-net"
                                    >
                                        ₹{Math.abs(liveNet.net).toLocaleString('en-IN')}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        {liveNet.net >= 0
                                            ? 'You owe Vijay Ras Bhandar'
                                            : liveNet.net < 0
                                            ? 'They owe you'
                                            : 'Balanced'}
                                    </div>
                                </div>
                                <Button
                                    onClick={compute}
                                    disabled={computing}
                                    data-testid="vr-compute-btn"
                                >
                                    {computing ? (
                                        <Loader2 size={16} className="mr-2 animate-spin" />
                                    ) : (
                                        <Calculator size={16} className="mr-2" />
                                    )}
                                    Save settlement snapshot
                                </Button>
                            </div>
                            {settlement && (
                                <div className="text-xs text-muted-foreground mt-3">
                                    Server-computed: In ₹{settlement.incoming_total.toLocaleString('en-IN')} · Out ₹
                                    {settlement.outgoing_total.toLocaleString('en-IN')} · Net ₹
                                    {settlement.net_owed.toLocaleString('en-IN')}
                                </div>
                            )}
                        </Card>
                    )}
                </>
            )}
        </div>
    );
}
